import { Icon } from '../../components/Icon';

export interface Clause {
  id: string;
  title: string;
  body: string;
}

/**
 * ترتيب البند بالعربية: البند الأول، الثاني، … ثم رقمًا بعد العاشر.
 *
 * العقود تُكتب هكذا لا بـ«البند ١٣»، والصياغة تتبع العدد لا يُلصق بها رقمٌ
 * فيُقرأ ترجمةً آلية.
 */
const ORDINALS = [
  'الأول',
  'الثاني',
  'الثالث',
  'الرابع',
  'الخامس',
  'السادس',
  'السابع',
  'الثامن',
  'التاسع',
  'العاشر',
];

export function clauseLabel(index: number): string {
  return ORDINALS[index] ? `البند ${ORDINALS[index]}` : `البند ${index + 1}`;
}

/** معرّفٌ محلّي يثبت عبر إعادة الترتيب فلا يفقد الحقل تركيزه أثناء الكتابة. */
export function newClauseId(): string {
  return `c${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export const emptyClause = (): Clause => ({ id: newClauseId(), title: '', body: '' });

/**
 * محرّر بنود العقد.
 *
 * الترتيب هو موضع البند في المصفوفة لا رقمٌ يُحفظ معه: رقمٌ مخزَّن يفترق عن
 * الترتيب عند أول إدراجٍ في الوسط فيصير في العقد بندان «ثالث». وكلُّ بندٍ
 * يملك زرَّ إدراجٍ بعده، فيُختار موضع الجديد بالضغط في مكانه لا بإضافته في
 * الآخر ثم رفعه مرارًا.
 */
export function ClauseEditor({
  clauses,
  onChange,
  disabled = false,
}: {
  clauses: Clause[];
  onChange: (next: Clause[]) => void;
  disabled?: boolean;
}) {
  const patch = (index: number, fields: Partial<Clause>) => {
    onChange(clauses.map((c, i) => (i === index ? { ...c, ...fields } : c)));
  };

  const insertAt = (index: number) => {
    const next = [...clauses];
    next.splice(index, 0, emptyClause());
    onChange(next);
  };

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= clauses.length) return;
    const next = [...clauses];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved!);
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(clauses.filter((_, i) => i !== index));
  };

  return (
    <div className="clauses">
      {clauses.length === 0 && (
        <p className="mute tiny center">لا بنود بعد. أضِف البند الأول.</p>
      )}

      {clauses.map((c, i) => (
        <div key={c.id} className="clause">
          <div className="clause-head">
            <span className="clause-no">{clauseLabel(i)}</span>

            {!disabled && (
              <span className="row" style={{ gap: 2, marginInlineStart: 'auto' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost"
                  title="تحريك لأعلى"
                  aria-label={`تحريك ${clauseLabel(i)} لأعلى`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <Icon name="chevronUp" size={14} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost"
                  title="تحريك لأسفل"
                  aria-label={`تحريك ${clauseLabel(i)} لأسفل`}
                  disabled={i === clauses.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <Icon name="chevronDown" size={14} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost"
                  title="حذف البند"
                  aria-label={`حذف ${clauseLabel(i)}`}
                  onClick={() => remove(i)}
                >
                  <Icon name="trash" size={14} />
                </button>
              </span>
            )}
          </div>

          <input
            className="input clause-title"
            placeholder="عنوان البند (اختياري) — مثل: مدة العقد"
            value={c.title}
            disabled={disabled}
            onChange={(e) => patch(i, { title: e.target.value })}
          />
          <textarea
            className="textarea"
            rows={3}
            placeholder="نصّ البند"
            value={c.body}
            disabled={disabled}
            onChange={(e) => patch(i, { body: e.target.value })}
          />

          {/* الإدراج في الموضع: يُضاف البند حيث يُراد لا في الآخر ثم يُرفع. */}
          {!disabled && (
            <button
              type="button"
              className="clause-insert"
              onClick={() => insertAt(i + 1)}
              title={`إدراج بندٍ بعد ${clauseLabel(i)}`}
            >
              <Icon name="plus" size={13} />
              إدراج بندٍ هنا
            </button>
          )}
        </div>
      ))}

      {!disabled && (
        <button
          type="button"
          className="btn btn-sm"
          style={{ width: '100%' }}
          onClick={() => insertAt(clauses.length)}
        >
          <Icon name="plus" size={14} />
          {clauses.length === 0 ? 'أضِف البند الأول' : 'أضِف بندًا في الآخر'}
        </button>
      )}
    </div>
  );
}
